// Pure-browser replacement for the Electron preload's `window.api`.
//
// All methods route either to the in-browser `Vehicles` singleton (for
// commands) or to `eventBus` (for events that the original main process
// emitted via `mainWindow.webContents.send(...)`).

import vehicles from './vehicles_browser.js';
import eventBus from './event_bus.js';
import WebSerial from './web_serial.js';
import browserFs from './browser_fs.js';
// ---- helpers ----

function downloadTextFile(filename, text) {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Known USB VID -> vendor name. The Web Serial API often returns no
// `usbVendorName` on macOS/Linux, so we maintain a small lookup of the
// chips and flight controllers commonly used with ArduPilot.
const KNOWN_USB_VENDORS = {
    0x0403: 'FTDI',
    0x067B: 'Prolific',
    0x10C4: 'Silicon Labs',   // CP210x family (CP2102 / CP2104 / CP2105)
    0x1A86: 'QinHeng',        // CH340 / CH341
    0x2341: 'Arduino',
    0x2A03: 'Arduino',
    0x239A: 'Adafruit',
    0x1B4F: 'SparkFun',
    0x2E8A: 'Raspberry Pi',
    0x0483: 'STMicro',
    0x1209: 'pid.codes',
    0x2DAE: 'CubePilot',
    0x3162: 'Holybro',
    0x35E4: 'mRo',
};

// Known VID:PID specifics that should take priority over generic chip names.
const KNOWN_USB_PRODUCTS = {
    '1209:5740': 'ArduPilot ChibiOS',
    '1209:5741': 'ArduPilot Bootloader',
    '0483:5740': 'ArduPilot ChibiOS',
    '0483:5741': 'ArduPilot Bootloader',
    '10C4:EA60': 'CP2102 USB-Serial',
    '10C4:EA70': 'CP2105 USB-Serial',
    '1A86:7523': 'CH340 USB-Serial',
    '1A86:55D4': 'CH9102 USB-Serial',
    '0403:6001': 'FTDI FT232',
    '0403:6014': 'FTDI FT232H',
    '0403:6015': 'FTDI FT-X',
    '067B:2303': 'PL2303 USB-Serial',
    '2DAE:1011': 'CubePilot Cube',
    '2DAE:1016': 'CubePilot Cube Orange',
    '3162:004B': 'Holybro Pixhawk',
};

// Build a friendly serial port list from Web Serial SerialPort objects.
// The Web Serial API does not expose system device paths, so we derive a
// human-readable label from whatever `getInfo()` returns. Chrome 110+
// surfaces `usbProductName` / `usbVendorName`; otherwise we fall back to
// the lookup tables above and finally to raw VID/PID. Identifiers are made
// unique by appending an index suffix when multiple ports share the same
// descriptor.
function buildSerialPortList(ports) {
    const hex4 = (n) => (n || 0).toString(16).padStart(4, '0').toUpperCase();
    const labelCounts = new Map();
    const base = ports.map((p) => {
        const info = (p.getInfo && p.getInfo()) || {};
        const vid = info.usbVendorId;
        const pid = info.usbProductId;
        const key = vid != null ? `${hex4(vid)}:${hex4(pid)}` : null;
        let label;
        if (info.usbProductName) {
            label = info.usbVendorName
                ? `${info.usbVendorName} ${info.usbProductName}`
                : info.usbProductName;
        } else if (key && KNOWN_USB_PRODUCTS[key]) {
            label = KNOWN_USB_PRODUCTS[key];
        } else if (vid != null && KNOWN_USB_VENDORS[vid]) {
            label = `${KNOWN_USB_VENDORS[vid]} (${key})`;
        } else if (vid != null) {
            label = `USB ${key}`;
        } else if (info.bluetoothServiceClassId) {
            label = `Bluetooth ${info.bluetoothServiceClassId}`;
        } else {
            label = 'Serial Port';
        }
        labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
        return { label, info, _port: p };
    });
    const seen = new Map();
    return base.map(({ label, info, _port }) => {
        let path = label;
        if (labelCounts.get(label) > 1) {
            const n = (seen.get(label) || 0) + 1;
            seen.set(label, n);
            path = `${label} #${n}`;
        }
        const manufacturer = info.usbVendorId != null
            ? `VID:${hex4(info.usbVendorId)} PID:${hex4(info.usbProductId)}`
            : '';
        return { path, manufacturer, _port };
    });
}

function pickTextFile(accept) {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        if (accept) input.accept = accept;
        input.onchange = (event) => {
            const file = event.target.files && event.target.files[0];
            if (!file) { resolve(null); return; }
            const reader = new FileReader();
            reader.onload = (e) => resolve({ file, text: e.target.result });
            reader.readAsText(file);
        };
        input.click();
    });
}

function pickBinaryFile(accept) {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        if (accept) input.accept = accept;
        input.onchange = (event) => {
            const file = event.target.files && event.target.files[0];
            if (!file) { resolve(null); return; }
            const reader = new FileReader();
            reader.onload = (e) => resolve({ file, bytes: new Uint8Array(e.target.result) });
            reader.readAsArrayBuffer(file);
        };
        input.click();
    });
}

function parseParamText(text) {
    const params = {};
    text.split('\n').forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) return;
        const parts = trimmed.split(/\s+/);
        const key = parts[0]; const value = parts[1];
        if (key && value !== undefined) {
            const num = parseFloat(value);
            params[key] = Number.isFinite(num) ? num : value;
        }
    });
    return params;
}

function formatParamFile(paramList) {
    let out = '';
    Object.keys(paramList).forEach((k) => {
        const p = paramList[k];
        const v = (p && typeof p === 'object' && p.value !== undefined) ? p.value : p;
        out += `${k}\t${v}\n`;
    });
    return out;
}

// ---- API surface ----

const api = {
    // Generic send (used by the renderer for a few legacy channels)
    send: (channel, ...args) => {
        switch (channel) {
            case 'load-params-from-file': {
                const mavId = args[0];
                pickTextFile('.param,.parm').then((picked) => {
                    if (!picked) return;
                    const params = parseParamText(picked.text);
                    eventBus.emit('param-loaded', mavId, params);
                });
                break;
            }
            case 'save-params-to-file': {
                const mavId = args[0];
                const vehicle = vehicles.getVehicleByMavId(mavId);
                if (!vehicle) return;
                const date = new Date();
                const name = `${vehicle.getMavTypeTargetName() || 'vehicle'}-${mavId}(${date.toLocaleDateString().replace(/\//g, '-')} ${date.toLocaleTimeString()}).param`;
                downloadTextFile(name, formatParamFile(vehicle.params));
                break;
            }
            default:
                console.warn('Unhandled api.send channel:', channel, args);
        }
    },

    // ---- File-picker stubs (used to live behind native dialogs) ----
    selectFolder: async () => {
        if (window.showDirectoryPicker) {
            try { return await window.showDirectoryPicker(); } catch (_) { return null; }
        }
        console.warn('Directory picker not supported in this browser');
        return null;
    },
    selectFile: () => pickTextFile().then(r => r && r.file ? r.file.name : null),
    selectFiles: () => pickTextFile().then(r => r && r.file ? [r.file.name] : null),

    // ---- MAVLink settings ----
    onMavlinkSettings: (cb) => eventBus.on('mavlink-settings', cb),
    removeOnMavlinkSettings: (cb) => eventBus.off('mavlink-settings', cb),
    getMavlinkSettings: () => {
        // Reply asynchronously so listeners attached after the call still fire.
        Promise.resolve().then(() => eventBus.emit('mavlink-settings', vehicles.getMavlinkSettings()));
    },
    saveMavlinkSettings: (settings) => {
        vehicles.saveMavlinkSettings(settings);
        eventBus.emit('mavlink-settings', vehicles.getMavlinkSettings());
    },

    // ---- Status streams ----
    onMavlinkMessage: (cb) => eventBus.on('mavlink-message', cb),
    removeOnMavlinkMessage: (cb) => eventBus.off('mavlink-message', cb),
    onModeList: (cb) => eventBus.on('mode-list', cb),
    onMavIdList: (cb) => eventBus.on('mav-id-list', cb),
    onVehiclesCleared: (cb) => eventBus.on('vehicles-cleared', cb),

    // ---- Parameters ----
    onParamList: (cb) => eventBus.on('param-list', cb),
    onParam: (cb) => eventBus.on('param', cb),
    onParamLoaded: (cb) => eventBus.on('param-loaded', cb),
    onParamProgress: (cb) => eventBus.on('param-progress', cb),
    removeOnParamProgress: (cb) => eventBus.off('param-progress', cb),

    // ---- Commands ----
    setMode: (mavId, mode) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.setMode(mode);
    },
    goTo: (mavId, lat, lon, alt, frame) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) { v.setGuidedMode(); v.goTo(lat, lon, alt, frame); }
    },
    setHome: (mavId, lat, lon, alt, frame = 3) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.setHome(lat, lon, alt, frame);
    },
    setRoi: (mavId, lat, lon, alt) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) { v.setGuidedMode(); v.setRoi && v.setRoi(lat, lon, alt); }
    },
    requestParamList: (mavId) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.requestParamList();
    },
    reboot: (mavId) => vehicles.reboot(mavId),
    armDisarm: (mavId, arm, force = false) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.armDisarm(arm, force);
    },
    setParams: (mavId, params) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.setParams(params); },
    resetParamsToDefault: (mavId) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) { v.resetParamsToDefault(); v.requestParamList(); }
    },

    // ---- Calibration ----
    calibrateGyro:       (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.calibrateGyro(); },
    calibrateBaro:       (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.calibrateBaro(); },
    calibrateAccelFull:  (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.calibrateAccelFull(); },
    calibrateAccelLevel: (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.calibrateAccelLevel(); },
    calibrateBoardLevel: (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.calibrateBoardLevel(); },
    calibrateAirspeed:   (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.calibrateAirspeed(); },
    cancelCalibration:   (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.cancelCalibration(); },
    accelCalVehiclePos:  (mavId, pos) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.accelCalVehiclePos(pos); },
    startMagCal:  (mavId, opts) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.startMagCal(opts); },
    acceptMagCal: (mavId, mask) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.acceptMagCal(mask); },
    cancelMagCal: (mavId, mask) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.cancelMagCal(mask); },

    // ---- Motor Test ----
    motorTest: (mavId, motorNumber, throttleValue, timeoutSec, opts) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) v.motorTest(motorNumber, throttleValue, timeoutSec, opts);
    },
    motorTestStop: (mavId, motorNumber) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) v.motorTestStop(motorNumber);
    },

    // ---- Legacy log download ----
    logRequestList: (mavId, start, end) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.logRequestList(start, end); },
    logRequestData: (mavId, id, ofs, count) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.logRequestData(id, ofs, count); },
    logRequestEnd:  (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.logRequestEnd(); },
    logErase:       (mavId) => { const v = vehicles.getVehicleByMavId(mavId); if (v) v.logErase(); },

    // ---- Connection health ----
    onFlightControllerConnectionHealth: (cb) => eventBus.on('flight-controller-connection-health', cb),
    removeOnFlightControllerConnectionHealth: (cb) => eventBus.off('flight-controller-connection-health', cb),
    onConnectionStatus: (cb) => eventBus.on('connection-status', cb),
    removeOnConnectionStatus: (cb) => eventBus.off('connection-status', cb),
    onRebootReconnectStatus: (cb) => eventBus.on('reboot-reconnect-status', cb),
    removeOnRebootReconnectStatus: (cb) => eventBus.off('reboot-reconnect-status', cb),

    // ---- Serial ports ----
    getAvailableSerialPorts: () => {
        WebSerial.listPorts().then((ports) => {
            eventBus.emit('available-serial-ports', buildSerialPortList(ports));
        });
    },
    onAvailableSerialPorts: (cb) => eventBus.on('available-serial-ports', cb),
    removeOnAvailableSerialPorts: (cb) => eventBus.off('available-serial-ports', cb),

    requestSerialPort: async () => {
        try {
            await WebSerial.requestPort();
        } catch (err) {
            // User-cancelled or unsupported. Surface silently.
            console.warn('requestSerialPort:', err && err.message);
            return;
        }
        // Re-list to surface the newly authorized port to the UI.
        WebSerial.listPorts().then((ports) => {
            eventBus.emit('available-serial-ports', buildSerialPortList(ports));
        });
    },

    connectFlightController: async (port) => {
        try {
            await vehicles.connect(port);
            eventBus.emit('connection-status', { success: true });
        } catch (err) {
            console.error('Connect failed:', err);
            eventBus.emit('connection-status', { success: false, message: err && err.message });
        }
    },
    disconnectFlightController: async () => {
        await vehicles.disconnect({ cancelReconnect: true });
        eventBus.emit('connection-status', {
            success: false,
            disconnected: true,
            manual: true,
            message: 'Disconnected by user.',
        });
    },

    // ---- MAVLink FTP ----
    mavFtpList: (mavId, ftpPath) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) v.mavlinkFTP.cmdList(ftpPath);
        else console.warn('mavFtpList: no vehicle', mavId);
    },
    onMavFtpListResponse: (cb) => eventBus.on('mav-ftp-list-response', cb),
    removeOnMavFtpListResponse: (cb) => eventBus.off('mav-ftp-list-response', cb),
    onceMavFtpListResponse: (cb) => eventBus.once('mav-ftp-list-response', cb),

    mavFtpget: (mavId, ftpPath) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (!v) return;
        // Use the file name from the FTP path; the browser_fs shim will
        // trigger a download when the session ends.
        const fname = (ftpPath || '').split('/').pop() || 'download.bin';
        v.mavlinkFTP.cmdGet(ftpPath, fname);
    },
    onMavFtpGetProgress: (cb) => eventBus.on('mav-ftp-get-progress', cb),
    removeOnMavFtpGetProgress: (cb) => eventBus.off('mav-ftp-get-progress', cb),

    mavFtpTerminateSession: (mavId) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (v) v.mavlinkFTP.cmdTerminateSession && v.mavlinkFTP.cmdTerminateSession();
    },
    onMavFtpTerminateSession: (cb) => eventBus.on('mav-ftp-terminate-session', cb),
    removeOnMavFtpTerminateSession: (cb) => eventBus.off('mav-ftp-terminate-session', cb),

    mavFtpRm: (mavId, ftpPath) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.mavlinkFTP.cmdRm(ftpPath);
    },
    mavFtpRmdir: (mavId, ftpPath) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.mavlinkFTP.cmdRmdir(ftpPath);
    },
    onceMavFtpRmResponse: (cb) => eventBus.once('mav-ftp-rm-response', cb),
    onceMavFtpRmdirResponse: (cb) => eventBus.once('mav-ftp-rmdir-response', cb),

    mavFtpRename: (mavId, oldPath, newPath) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.mavlinkFTP.cmdRename(oldPath, newPath);
    },
    onceMavFtpRenameResponse: (cb) => eventBus.once('mav-ftp-rename-response', cb),

    mavFtpMkdir: (mavId, ftpPath) => {
        const v = vehicles.getVehicleByMavId(mavId); if (v) v.mavlinkFTP.cmdMkdir(ftpPath);
    },
    onceMavFtpMkdirResponse: (cb) => eventBus.once('mav-ftp-mkdir-response', cb),

    mavFtpPut: async (mavId, ftpPath) => {
        const v = vehicles.getVehicleByMavId(mavId);
        if (!v) return;
        // Prompt the user for the local file to upload. `ftpPath` from
        // the editor is the *destination directory* on the vehicle --
        // append the picked file name to form the full FTP path.
        const picked = await pickBinaryFile();
        if (!picked) return;
        const { file, bytes } = picked;
        // Stash the bytes in browser_fs under the filename so the
        // existing cmdPut path (which uses fs.openSync('r') +
        // readSync) sees them.
        browserFs.registerFile(file.name, bytes);
        const baseDir = (ftpPath || '').replace(/\/+$/, '');
        const destPath = baseDir ? `${baseDir}/${file.name}` : `/${file.name}`;
        // Cleanup the pending registration once the upload finishes
        // (success or failure).
        const cleanup = () => browserFs.unregisterFile(file.name);
        eventBus.once('mav-ftp-put-response', cleanup);
        v.mavlinkFTP.cmdPut(file.name, destPath);
    },
    onceMavFtpPutResponse: (cb) => eventBus.once('mav-ftp-put-response', cb),
    onMavFtpPutProgress: (cb) => eventBus.on('mav-ftp-put-progress', cb),
    removeOnMavFtpPutProgress: (cb) => eventBus.off('mav-ftp-put-progress', cb),
};

window.api = api;

export default api;
